import { motion } from "framer-motion";
import { Star, BadgeCheck, ArrowUpRight } from "lucide-react";

export const REVIEWS_URL = "https://www.google.com/maps/search/?api=1&query=His+and+Hers+Cleaning+Services+and+Handyman+Port+St.+Lucie+FL";
export const ANGI_URL = "https://www.angi.com/companylist/us/fl/port-st-lucie/his-and-hers-cleaning-sevices-reviews-10101838.htm";

const REVIEWS = [
  {
    text: "A neighbor referral that turned into a repeat customer — thorough, detail-oriented, and careful with our home and belongings on every visit.",
    tag: "Verified Google Review",
  },
  {
    text: "Over a year of regular service and counting. Professional, thorough, and trustworthy enough that we're comfortable giving them access when we're away.",
    tag: "Verified Google Review",
  },
  {
    text: "They've helped us through pregnancy, a newborn, and now a 9-month-old — always professional, thorough, and dependable no matter what's going on at home.",
    tag: "Verified Google Review",
  },
  {
    text: "From repairs to deep cleaning, the attention to detail stood out. Reliable, friendly, and it's clear they take pride in the work.",
    tag: "Verified Google Review",
  },
  {
    text: "Everything on our list got done, on time and at a fair price. 100% satisfied and would recommend without hesitation.",
    tag: "Verified Google Review",
  },
];

function Stars() {
  return (
    <span className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={14} className="text-[#D97706] fill-[#D97706]" />
      ))}
    </span>
  );
}

export default function Reviews() {
  return (
    <section data-testid="reviews-section" className="relative bg-[#FDFBF7] pt-14 lg:pt-16 pb-20 lg:pb-24 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-8 mb-16"
        >
          <div>
            <span className="font-jet text-[11px] uppercase tracking-[0.3em] text-[#993B19]">Reviews</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#0F172A]">
              Trusted By Our <em className="italic text-[#D96B43]">Neighbors.</em>
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-4 bg-white border border-[#D96B43]/15 rounded-2xl px-6 py-4 shadow-sm" data-testid="google-rating-badge">
              <img src="/images/google-g.svg" alt="Google logo" className="w-12 h-12 object-contain" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-4xl text-[#0F172A] leading-none">5.0</span>
                  <Stars />
                </div>
                <span className="block font-jet text-[10px] uppercase tracking-[0.2em] text-[#64748B] mt-1.5">on Google</span>
              </div>
            </div>
            <a
              href={ANGI_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="angi-rating-badge"
              className="flex items-center gap-4 bg-white border border-[#D96B43]/15 rounded-2xl px-6 py-4 shadow-sm hover:border-[#D96B43]/60 hover:shadow-lg transition-all duration-300"
            >
              <img src="/images/angi.svg" alt="Angi logo" className="w-14 h-12 object-contain" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-4xl text-[#0F172A] leading-none">4.8</span>
                  <Stars />
                </div>
                <span className="block font-jet text-[10px] uppercase tracking-[0.2em] text-[#64748B] mt-1.5">on Angi</span>
              </div>
            </a>
          </div>
        </motion.div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:balance]">
          {REVIEWS.map((r, i) => (
            <motion.blockquote
              key={i}
              data-testid={`review-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.1 }}
              className="break-inside-avoid mb-6 bg-white rounded-3xl border border-[#D96B43]/15 p-7 hover:border-[#D96B43]/50 hover:shadow-xl hover:shadow-[#D96B43]/5 transition-all duration-500"
            >
              <Stars />
              <p className="mt-4 text-[#0F172A] leading-relaxed text-[15px]">“{r.text}”</p>
              <footer className="mt-5 flex items-center gap-2 font-jet text-[10px] uppercase tracking-[0.18em] text-[#64748B]">
                <BadgeCheck size={14} className="text-[#D96B43]" />
                {r.tag}
              </footer>
            </motion.blockquote>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.2 }}
          className="mt-10 flex flex-wrap items-center gap-8"
        >
          <a
            data-testid="reviews-google-link"
            href={REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 font-jet text-xs uppercase tracking-[0.2em] text-[#0F172A] hover:text-[#D96B43] transition-colors duration-300"
          >
            See All Our Google Reviews
            <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </a>
          <a
            data-testid="reviews-angi-link"
            href={ANGI_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 font-jet text-xs uppercase tracking-[0.2em] text-[#0F172A] hover:text-[#D96B43] transition-colors duration-300"
          >
            See Our Angi Reviews
            <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
